import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";

// Aligned with the finalized FD2G framework (owner-approved 2026-10-02):
// the Five Voices Framework is NOTICE → LISTEN → UNDERSTAND → RESPOND → HONOR.
// "The Five Voices of the Journey" is the branded certification/course title.
// No pricing, enrollment dates, CE credits, accreditation, testimonials,
// statistics, professional claims, or CCCHAPS branding on this page.

const ctaCls =
  "inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 whitespace-nowrap";

function Principle({ children }) {
  return (
    <blockquote className="border-l-2 border-gold pl-5 sm:pl-6 font-serif italic text-lg sm:text-xl text-navy leading-snug my-6">
      {children}
    </blockquote>
  );
}

function Bullets({ items, cols = 2 }) {
  return (
    <ul
      className={`grid gap-x-8 gap-y-2 ${cols === 2 ? "sm:grid-cols-2" : ""}`}
    >
      {items.map((t) => (
        <li key={t} className="flex gap-3 leading-relaxed">
          <span className="shrink-0 font-serif text-gold">—</span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          The Five Voices of the Journey
        </p>
        <h1 className="mt-5 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
          Certified Facilitator Training for From Diagnosis to Goodbye
        </h1>
        <p className="mt-6">
          <span className="inline-block text-xs uppercase tracking-[0.18em] text-mist border border-line-strong rounded-full px-4 py-1.5">
            Course Code: FD2G-FAC-001
          </span>
        </p>
        <p className="mt-8 max-w-3xl text-lg leading-relaxed text-stone2">
          Learn to facilitate meaningful conversations around serious illness,
          caregiving, end of life, and grief while protecting the voices of
          the people living the journey.
        </p>
        <div className="mt-6 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            The From Diagnosis to Goodbye Facilitator Training prepares
            participants to responsibly guide conversations, groups,
            workshops, and learning experiences using the principles and
            approved resources of From Diagnosis to Goodbye.
          </p>
          <p>
            This training is not about becoming the expert in someone else's
            experience. It is about learning how to recognize the voices
            already present, create space for those voices to be heard, and
            facilitate without taking over the conversation.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div
          className="mt-16 bg-navy-deep rounded-md px-8 sm:px-14 py-14 sm:py-20 text-center"
          data-testid="training-hero-statement"
        >
          <p className="max-w-3xl mx-auto font-serif italic text-2xl sm:text-4xl leading-snug text-ivory">
            &ldquo;The facilitator's voice should never become the{" "}
            <em className="text-gold">loudest voice</em> in the room.&rdquo;
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function Why() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Why This Training Exists
          </p>
          <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
            <p>From Diagnosis to Goodbye was intentionally written through stories.</p>
            <p>
              Those stories allow readers to encounter serious illness,
              caregiving, dying, death, and grief through human experience—not
              simply through information, instruction, or clinical language.
            </p>
            <p>
              A facilitator's responsibility is not to rush in and explain
              what a story means.
            </p>
            <p>
              It is to help participants encounter the story, recognize the
              voices within it, consider what it awakens in them, and make
              room for reflection.
            </p>
          </div>
          <Principle>
            &ldquo;The story is the doorway. The facilitator does not need to
            become the destination.&rdquo;
          </Principle>
          <p className="max-w-3xl text-lg leading-relaxed text-stone2">
            This training teaches facilitators how to preserve that approach
            when bringing From Diagnosis to Goodbye into groups,
            organizations, churches, hospices, healthcare settings, caregiver
            programs, bereavement programs, and community conversations.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const VOICES = [
  {
    n: "01",
    name: "Patient",
    quote: "This is happening to me.",
    body: "Recognize the patient's words, choices, preferences, silence, behavior, previously expressed wishes, and right not to engage.",
  },
  {
    n: "02",
    name: "Family",
    quote: "This is happening to someone I love.",
    body: "Recognize what family members may be carrying while trying to support someone they love.",
  },
  {
    n: "03",
    name: "Caregiver",
    quote: "I am trying to hold everything together.",
    body: "Recognize the practical, emotional, relational, and anticipatory burdens that may accompany caregiving.",
  },
  {
    n: "04",
    name: "Professional or Helper",
    quote: "I am here to help.",
    body: "Recognize both the value and the influence carried by professionals, pastors, chaplains, volunteers, facilitators, and others in helping roles.",
    principle: "Expertise should inform the room without owning the room.",
  },
  {
    n: "05",
    name: "Grief",
    quote: "Something is changing, and I am already losing something.",
    body: "Recognize that grief may begin before death and may involve losses of independence, identity, relationships, roles, abilities, plans, home, and the future someone expected.",
  },
];

function VoicesInJourney() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Learn to Hear the Voices in the Journey
        </p>
        <h2 className="mt-5 max-w-4xl font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.15]">
          Throughout the training, facilitators learn to recognize the Voices
          in the Journey that may be present during serious illness,
          caregiving, end of life, and grief.
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {VOICES.map((v, i) => (
          <Reveal
            key={v.n}
            delay={0.06 * i}
            className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}
          >
            <div
              className="h-full bg-white border border-line rounded-md p-8 hover:border-gold/60 transition-all duration-300"
              data-testid={`training-voice-card-${i + 1}`}
            >
              <span className="font-serif text-gold text-lg">{v.n}</span>
              <h3 className="mt-4 font-serif text-xl sm:text-2xl text-navy leading-snug">
                {v.name}
              </h3>
              <p className="mt-4 font-serif italic text-gold">
                &ldquo;{v.quote}&rdquo;
              </p>
              <p className="mt-4 text-stone2 leading-relaxed text-[15px]">
                {v.body}
              </p>
              {v.principle && (
                <p className="mt-4 border-t border-line pt-4 font-serif italic text-navy text-[15px]">
                  &ldquo;{v.principle}&rdquo;
                </p>
              )}
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-12 max-w-3xl space-y-3 text-lg leading-relaxed text-stone2">
          <p>One person may carry several of these voices at the same time.</p>
          <p className="font-serif text-xl text-navy">
            The Voices in the Journey are not boxes for categorizing people.
            They are a way of becoming more attentive to the human experiences
            present in the room.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

const FRAMEWORK = ["NOTICE", "LISTEN", "UNDERSTAND", "RESPOND", "HONOR"];

function FrameworkIntro() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            The Facilitation Framework
          </p>
          <div className="mt-6 max-w-3xl font-serif text-xl sm:text-2xl text-navy leading-relaxed">
            <p>
              The voices in the room tell facilitators who and what to hear.
            </p>
            <p className="mt-3">
              The Five Voices Framework teaches them how to respond.
            </p>
          </div>
          <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-3">
            {FRAMEWORK.map((w, i) => (
              <span key={w} className="flex items-center gap-3">
                <span className="font-serif text-lg text-navy tracking-[0.14em] border border-line-strong rounded-full px-4 py-1.5 bg-ivory">
                  {w}
                </span>
                {i < FRAMEWORK.length - 1 && (
                  <span className="text-gold font-serif">→</span>
                )}
              </span>
            ))}
          </p>
          <p className="mt-8 text-stone2">
            Each part of the framework becomes one training session.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Session({ num, label, word, sub, core, children }) {
  return (
    <div
      className="bg-white border border-line rounded-md p-7 sm:p-10"
      data-testid={`training-session-${word.toLowerCase()}`}
    >
      <div className="flex items-baseline gap-4">
        <span className="font-serif text-gold text-xl">{num}</span>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-mist">{label}</p>
          <h3 className="mt-1 font-serif text-2xl sm:text-3xl text-navy tracking-tight">
            {word}
          </h3>
          <p className="mt-1 font-serif italic text-gold">{sub}</p>
        </div>
      </div>
      <blockquote className="mt-7 border-l-2 border-gold pl-5 sm:pl-6">
        <p className="text-xs uppercase tracking-[0.18em] text-mist not-italic">
          Core principle
        </p>
        <p className="mt-2 font-serif italic text-lg sm:text-xl text-navy leading-snug">
          &ldquo;{core}&rdquo;
        </p>
      </blockquote>
      <div className="mt-7 space-y-4 text-stone2 leading-relaxed">{children}</div>
    </div>
  );
}

function Sessions() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          The Five Sessions
        </p>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
          One framework, five learning sessions.
        </h2>
      </Reveal>

      <div className="mt-12 space-y-8">
        <Reveal>
          <Session
            num="01"
            label="Session One"
            word="NOTICE"
            sub="Seeing the Voices Already in the Room"
            core="Notice what is present before deciding what is needed."
          >
            <p>
              Before deciding what to say or do, facilitators learn to
              recognize what is already happening.
            </p>
            <div>
              <p className="font-medium text-navy">Participants learn to notice:</p>
              <div className="mt-3">
                <Bullets
                  items={[
                    "Which voices appear to be present",
                    "Which voice may be dominant",
                    "Which voices may be quieter or missing",
                    "Verbal and nonverbal communication",
                    "Changes in participation",
                    "The facilitator's own reactions, assumptions, experiences, and desire to help",
                  ]}
                />
              </div>
            </div>
            <p>
              Facilitators also learn an important distinction between
              observation and interpretation.
            </p>
            <Bullets
              cols={1}
              items={[
                "Tears do not automatically tell us what someone is feeling.",
                "Silence does not automatically mean denial.",
                "Anger does not automatically mean spiritual distress.",
                "A lack of tears does not automatically mean acceptance.",
              ]}
            />
            <Principle>
              &ldquo;Observation belongs to the facilitator. Meaning belongs
              first to the person experiencing it.&rdquo;
            </Principle>
            <p>
              The first responsibility of the facilitator is not to determine
              what something means.
            </p>
            <p>
              It is to notice carefully enough to create room for the person
              to tell us.
            </p>
          </Session>
        </Reveal>

        <Reveal>
          <Session
            num="02"
            label="Session Two"
            word="LISTEN"
            sub="Creating Space Without Taking Over"
            core="Listen to understand the person's experience—invitation opens the door without pulling anyone through it."
          >
            <p>
              Noticing someone's response does not automatically give a
              facilitator permission to enter that person's story.
            </p>
            <p>
              Participants learn how to invite conversation without
              interrogation, pressure, assumption, or forced disclosure.
            </p>
            <div>
              <p className="font-medium text-navy">Training includes:</p>
              <div className="mt-3">
                <Bullets
                  items={[
                    "Asking open and non-steering questions",
                    "Inviting without demanding participation",
                    "Respecting someone's right not to speak",
                    "Making room for quieter participants without putting them on the spot",
                    "Recognizing when not to ask another question",
                    "Using the book's stories as openings for reflection",
                    "Respecting different ways of participating",
                  ]}
                />
              </div>
            </div>
            <Principle>
              &ldquo;A person's silence does not create an obligation to
              disclose.&rdquo;
              <br />
              &ldquo;Not every disclosure requires another question.&rdquo;
            </Principle>
            <p>
              Listening is more than remaining quiet while another person
              speaks.
            </p>
            <p>
              Facilitators learn to listen for the person's words, emotions,
              values, concerns, changes, questions, and meaning without
              assuming they already understand the experience.
            </p>
            <div>
              <p className="font-medium text-navy">
                Participants examine common barriers to listening, including:
              </p>
              <div className="mt-3">
                <Bullets
                  items={[
                    "Trying to fix the situation",
                    "Offering reassurance too quickly",
                    "Comparing experiences",
                    "Correcting the person",
                    "Teaching before listening",
                    "Redirecting the conversation",
                    "Allowing the facilitator's own story to compete with the participant's story",
                  ]}
                />
              </div>
            </div>
            <Principle>
              &ldquo;Listen for the person inside the story, not merely the
              events of the story.&rdquo;
            </Principle>
            <p>
              Participants practice appropriate reflection, clarification,
              acknowledgment, and silence.
            </p>
            <p>
              Participants learn that good facilitation does not measure
              success by how much people disclose.
            </p>
            <p className="font-serif text-xl text-navy">
              Listening is also participation.
            </p>
          </Session>
        </Reveal>

        <Reveal>
          <Session
            num="03"
            label="Session Three"
            word="UNDERSTAND"
            sub="Hearing What Is Said—and What May Be Beneath It"
            core="Understanding helps people discover meaning. It does not assign meaning to them."
          >
            <p>
              Understanding looks beneath the words—to the story, perspective,
              relationships, fears, values, beliefs, and circumstances behind
              the voice. Reflection and curiosity belong here.
            </p>
            <p>
              This session teaches facilitators how to help participants
              understand what was said—without prescribing what they should
              have learned, felt, believed, or decided.
            </p>
            <div>
              <p className="font-medium text-navy">
                Participants learn to use questions such as:
              </p>
              <div className="mt-3">
                <Bullets
                  cols={1}
                  items={[
                    "What stood out to you?",
                    "Was there something you saw differently?",
                    "What are you still thinking about?",
                    "Did anything connect with your own experience?",
                    "Is there something you want to carry forward from this conversation?",
                  ]}
                />
              </div>
            </div>
            <p>
              Facilitators learn why questions beginning with assumptions
              such as &ldquo;Don't you think...&rdquo; can become persuasion
              disguised as reflection.
            </p>
            <Principle>
              &ldquo;A reflective question should leave room for an answer
              the facilitator did not expect.&rdquo;
            </Principle>
            <p>
              A good reflection gives the person's story back to them—it does
              not rewrite it for them.
            </p>
            <p>
              Participants also learn that every conversation does not
              require immediate resolution.
            </p>
            <p>
              <em className="font-serif text-navy">
                &ldquo;An unfinished question is not necessarily a failed
                conversation.&rdquo;
              </em>
            </p>
            <p>The goal is not to manufacture closure.</p>
            <p>
              It is to help people leave the conversation with their dignity,
              autonomy, and voice intact.
            </p>
          </Session>
        </Reveal>

        <Reveal>
          <Session
            num="04"
            label="Session Four"
            word="RESPOND"
            sub="Responding to What the Conversation Needs"
            core="Respond to what the conversation needs—without taking over, correcting, fixing, diagnosing, or exceeding the facilitator's role."
          >
            <p>
              Facilitators learn to determine what the conversation needs and
              to respond within the role they actually hold.
            </p>
            <Principle>
              &ldquo;Good facilitation shares the space without surrendering
              responsibility for the space.&rdquo;
            </Principle>
            <p>
              Facilitators also learn to remain within the role they actually
              hold:{" "}
              <em className="font-serif text-navy">
                &ldquo;Facilitate within the role you actually hold—not the
                role the situation tempts you to assume.&rdquo;
              </em>
            </p>
            <div>
              <p className="font-medium text-navy">Participants learn about:</p>
              <div className="mt-3">
                <Bullets
                  items={[
                    "Unsolicited advice",
                    "Dominating or correcting participants",
                    "Emotional distress",
                    "Situations requiring referral or another level of support",
                    "Recognizing when facilitation is no longer enough",
                  ]}
                />
              </div>
            </div>
            <p>
              The training does not ask facilitators to become the solution
              to every need that emerges.
            </p>
            <p>
              Sometimes responsible facilitation means recognizing when
              another kind of support is needed.
            </p>
          </Session>
        </Reveal>

        <Reveal>
          <Session
            num="05"
            label="Session Five"
            word="HONOR"
            sub="Holding the Person, the Story, and the Boundaries"
            core="If a person's story enters the room, the facilitator has a responsibility to help protect how that story is held."
          >
            <p>
              Facilitators have a responsibility to help create an
              environment where people can participate without having their
              stories corrected, controlled, exploited, spiritualized,
              diagnosed, or turned into someone else's lesson.
            </p>
            <div>
              <p className="font-medium text-navy">Participants learn about:</p>
              <div className="mt-3">
                <Bullets
                  items={[
                    "Emotional and relational safety",
                    "Participant autonomy",
                    "Privacy expectations",
                    "Spiritual and cultural differences",
                    "Appropriate facilitator boundaries",
                  ]}
                />
              </div>
            </div>
            <p>
              To honor someone's voice is to keep their dignity, autonomy,
              and story intact—carried carefully, and never used without
              their standing.
            </p>
          </Session>
        </Reveal>
      </div>
    </section>
  );
}

const GUIDING = [
  { word: "NOTICE", q: "What is already happening here?" },
  { word: "LISTEN", q: "How can I create space and listen without forcing disclosure?" },
  { word: "UNDERSTAND", q: "What might I not yet understand about the story behind this voice?" },
  { word: "RESPOND", q: "What does this conversation need—and what does it need from me?" },
  { word: "HONOR", q: "What has been entrusted to this space, and how do I protect it?" },
];

function Together() {
  return (
    <section className="bg-navy-deep">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Bringing the Framework Together
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-ivory tracking-tight">
            By the end of the five sessions, facilitators learn to ask five
            guiding questions:
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {GUIDING.map((g, i) => (
            <Reveal key={g.word} delay={0.06 * i} className="h-full">
              <div
                className="h-full border-t-2 border-gold pt-5"
                data-testid={`training-guiding-${g.word.toLowerCase()}`}
              >
                <p className="font-serif text-xl text-gold tracking-[0.1em]">
                  {g.word}
                </p>
                <p className="mt-3 text-sm text-ivory/80 leading-relaxed">
                  {g.q}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-3">
            {FRAMEWORK.map((w, i) => (
              <span key={w} className="flex items-center gap-3">
                <span className="font-serif text-lg text-ivory tracking-[0.14em] border border-gold/40 rounded-full px-4 py-1.5">
                  {w}
                </span>
                {i < FRAMEWORK.length - 1 && (
                  <span className="text-gold font-serif">→</span>
                )}
              </span>
            ))}
          </p>
          <p className="mt-8 text-ivory/70 max-w-3xl leading-relaxed">
            This becomes the practical facilitation framework for the From
            Diagnosis to Goodbye program.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function HowYouLearn() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          How You Learn
        </p>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
          This is not designed as a lecture-only course.
        </h2>
        <p className="mt-8 text-lg text-stone2">
          Participants learn through a combination of:
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {[
            "Book-based teaching",
            "Realistic facilitation scenarios",
            "Decision-based learning",
            "Guided reflection",
            "Practical application",
            "Session assessments",
            "A final Facilitator Certification Assessment",
          ].map((m) => (
            <span
              key={m}
              className="bg-sand border border-line rounded-full px-5 py-2.5 text-sm text-navy"
            >
              {m}
            </span>
          ))}
        </div>
        <p className="mt-10 max-w-3xl text-stone2 leading-relaxed">
          Scenarios require participants to consider what they would actually
          do when someone's voice becomes quiet, another participant begins
          dominating the conversation, grief surfaces unexpectedly, spiritual
          beliefs differ, emotional distress appears, or a situation moves
          beyond the facilitator's role.
        </p>
        <div className="mt-8 max-w-3xl font-serif text-xl text-navy leading-relaxed">
          <p>The purpose is not simply to remember information.</p>
          <p className="mt-2">
            It is to learn how to responsibly carry the work into real
            conversations.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

const NOT_QUALIFY = [
  "Licensed counselor",
  "Therapist",
  "Healthcare professional",
  "Hospice professional",
  "Professional chaplain",
  "Clinical spiritual-care provider",
  "Crisis professional",
];

function Scope() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                What This Training Is — and What It Is Not
              </p>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight leading-[1.15]">
                This training prepares participants to facilitate the
                approved From Diagnosis to Goodbye program.
              </h2>
              <p className="mt-8 text-stone2 leading-relaxed">
                It does not independently qualify someone as a:
              </p>
              <div className="mt-4">
                <Bullets items={NOT_QUALIFY} />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="bg-ivory border border-line rounded-md p-8">
                <p className="text-stone2 leading-relaxed">
                  Participants who already hold professional credentials
                  continue to practice according to the scope and standards
                  of those credentials.
                </p>
                <p className="mt-4 text-stone2 leading-relaxed">
                  The Certified From Diagnosis to Goodbye Facilitator
                  credential applies specifically to the responsible
                  facilitation of this program.
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.05}>
          <div className="mt-16 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
              From Training to Certification
            </p>
            <p className="mt-6 text-stone2 leading-relaxed">
              Completing the five learning sessions is an important part of
              certification, but course completion alone does not
              automatically confer the credential.
            </p>
            <p className="mt-4 text-stone2 leading-relaxed">
              Certification also requires completion of the required session
              assessments, passing the final Facilitator Certification
              Assessment with a score of at least 80%, affirming the
              Certified Facilitator Code of Ethics, and accepting the
              Authorized Facilitator Agreement.
            </p>
            <div
              className="mt-8 bg-navy-deep rounded-md px-8 py-10 text-center"
              data-testid="training-credential-callout"
            >
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Successful participants receive the credential
              </p>
              <p className="mt-4 font-serif text-2xl sm:text-3xl text-ivory leading-snug">
                Certified From Diagnosis to Goodbye Facilitator
              </p>
            </div>
            <p className="mt-6 text-stone2 leading-relaxed">
              Certification is issued for 12 months and is maintained through
              the program's annual renewal process.
            </p>
            <Link
              to="/facilitators/certification"
              data-testid="training-cta-certification-top"
              className={`${ctaCls} mt-8`}
            >
              View Certification Requirements <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Learn to Carry the Work Without Taking It Over
        </p>
        <div className="mt-8 max-w-3xl mx-auto space-y-5 font-serif text-2xl sm:text-3xl text-navy leading-snug">
          <p>
            The purpose of this training is not to teach facilitators to have
            every answer.
          </p>
          <p>
            It is to prepare them to recognize when another person's voice
            needs room.
          </p>
        </div>
        <p className="mt-10 font-serif italic text-xl text-gold">
          See the person. Hear the voice. Honor the journey.
        </p>
        <div className="mt-10">
          <Link
            to="/facilitators/certification"
            data-testid="training-cta-certification-bottom"
            className={ctaCls}
          >
            View Certification Requirements <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Link
          to="/facilitators"
          data-testid="training-back"
          className="mt-8 inline-flex items-center gap-2 text-sm text-stone2 hover:text-gold transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Facilitator Program
          Overview
        </Link>
      </Reveal>
    </section>
  );
}

export default function FacilitatorTraining() {
  return (
    <div className="bg-ivory" data-testid="facilitators-training-page">
      <Hero />
      <Why />
      <VoicesInJourney />
      <FrameworkIntro />
      <Sessions />
      <Together />
      <HowYouLearn />
      <Scope />
      <Closing />
    </div>
  );
}
